/* eslint-disable unicorn/prevent-abbreviations */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, pairwise, switchMap } from 'rxjs/operators';
import { scaleOrdinal } from '@visx/scale';
import { useDispatch } from 'react-redux';

import { colors, useTheme } from '@iq/react-components';

import config from '../../../config';
import { useAsync, useSelect$, useSubscribe } from '../../../utils';
import { getEvents, getUserEvents, getEventBuckets, getUniquePointGroups } from '../../../services';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../bundles/notifications';
import { getPollingDateRange, getRangeFromLabel } from '../../../bundles/application';
import { getUserEventTypes, getIotUpdated } from '../../../bundles/events';
import {
  getStaticComponents,
  getComponentHashmap,
  getScopedComponents,
  getComponentsUpdatedAt,
  getFilteredActiveComponentId,
} from '../../../bundles/components';
import { useSetLoading } from '../../LoadingLayer';
import { EVENT_TYPE_USER, EVENT_TYPE_DTMP, EVENT_TYPE_MACH } from '../../../constants';
import { normalizeEventTypeSelection } from './eventTypeSelection';

const iotSeverities = new Set(['critical', 'warning', 'minor', 'info', 'internal']);

const context = createContext();

const getUserIdentityFilters = (filterParameters) =>
  config.onPremise
    ? {}
    : {
        ...(filterParameters?.user?.assignee && {
          assignedTo: filterParameters.user.assignee,
        }),
        ...(filterParameters?.user?.creator && { users: filterParameters.user.creator }),
      };

export function getInitialRelatedEventTypesSelection(enabledEventTypes, filterParams = {}) {
  const normalizedEnabledEventTypes = normalizeEventTypeSelection(enabledEventTypes);
  const hasSavedShowFlags =
    typeof filterParams?.user?.showUserEvents === 'boolean' ||
    typeof filterParams?.mach?.showMachEvents === 'boolean' ||
    typeof filterParams?.dtmp?.showDtmpEvents === 'boolean';

  if (!hasSavedShowFlags) {
    return normalizedEnabledEventTypes;
  }

  const selectedFromSavedFlags = [
    filterParams?.mach?.showMachEvents && EVENT_TYPE_MACH,
    filterParams?.dtmp?.showDtmpEvents && EVENT_TYPE_DTMP,
    filterParams?.user?.showUserEvents && EVENT_TYPE_USER,
  ]
    .filter(Boolean)
    .filter((eventType) => normalizedEnabledEventTypes.includes(eventType));

  if (selectedFromSavedFlags.length > 0) {
    return selectedFromSavedFlags;
  }

  // If saved flags exist but all are false/invalid, keep one family enabled.
  return normalizedEnabledEventTypes.slice(0, 1);
}

/**
 * Event timeline provider that manages the local state of Event Timeline
 * @typedef { number } UtcTimestamp
 * @param {{
 *  site: <Site obj>{},
 *  dateDomain: [UtcTimestamp, UtcTimestamp, string],
 *  brushBucketCount: number,
 *  timelineBucketCount: number,
 *  componentScope: {
 *    selectedScope: boolean,
 *    activeComponentTypeFilter: string[],
 *  },
 *  filterText: string,
 *  filterParams: {
 *    mach: { eventType: string[] },
 *    dtmp: { algorithmTypes: string[], eventType: string[] },
 *    user: {
 *     type: string[],
 *    assignee: string,
 *    creator: string,
 *    tags: string[],
 *    status: string,
 *    activity: string,
 *  },
 *  children: any,
 *  availableIotEventTypes: { mach: Array, dtmp: { eventTypes: Array } },
 *  availableUserEventTypes: string[],
 *  hideInactiveSystemEvents: boolean,
 *  selectedEventType: string,
 *  enabledEventTypes: string[],
 * }} props
 */
export function EventTimelineProvider({
  site,
  dateDomain,
  brushBucketCount = 25,
  timelineBucketCount = 25,
  componentScope = {},
  eventListOrdering = 'desc',
  filterText,
  filterParams,
  children,
  availableIotEventTypes = { mach: [], dtmp: [] },
  availableUserEventTypes = [],
  hideInactiveSystemEvents,
  selectedEventType = '',
  enabledEventTypes = [],
  falseFilter,
  syncFilterParams = true,
}) {
  // State subjects
  const site$ = useMemo(() => new BehaviorSubject(site), []);
  useEffect(() => {
    if (site !== site$.value) {
      site$.next(site);
    }
  }, [site, site$]);

  const dateDomain$ = useMemo(() => new BehaviorSubject(dateDomain), []);
  useEffect(() => {
    if (dateDomain$.value !== dateDomain) {
      dateDomain$.next(dateDomain);
    }
  }, [dateDomain]);

  const filterText$ = useMemo(() => new BehaviorSubject(filterText), []);
  const availableUserEventTypes$ = useMemo(() => new BehaviorSubject(availableUserEventTypes), []);
  const availableIotEventTypes$ = useMemo(() => new BehaviorSubject(availableIotEventTypes), []);
  const selectedEventType$ = useMemo(() => new BehaviorSubject(selectedEventType), []);
  const enabledEventTypes$ = useMemo(() => new BehaviorSubject(enabledEventTypes), []);
  const relatedEventTypes$ = useMemo(
    () =>
      new BehaviorSubject(getInitialRelatedEventTypesSelection(enabledEventTypes, filterParams)),
    []
  );
  const falseFilter$ = useMemo(() => new BehaviorSubject(falseFilter), []);
  const filterParameters$ = useMemo(() => new BehaviorSubject(filterParams), []);
  const dtmpAlgorithmTypes$ = useMemo(() => new BehaviorSubject([]), []);
  const hideInactiveSystemEvents$ = useMemo(
    () => new BehaviorSubject(hideInactiveSystemEvents),
    []
  );
  const pointGroups$ = useMemo(() => new BehaviorSubject([]), []);

  useEffect(() => {
    if (!syncFilterParams) {
      return;
    }

    if (filterParams !== filterParameters$.value) {
      filterParameters$.next(filterParams);
    }
  }, [syncFilterParams, filterParams, filterParameters$]);

  useEffect(() => {
    if (filterText !== filterText$.value) {
      filterText$.next(filterText);
    }
  }, [filterText, filterText$]);

  useEffect(() => {
    if (selectedEventType !== selectedEventType$.value) {
      selectedEventType$.next(selectedEventType);
    }
  }, [selectedEventType, selectedEventType$]);

  useEffect(() => {
    const normalizedSelectedEventTypes = normalizeEventTypeSelection(enabledEventTypes);

    if (JSON.stringify(normalizedSelectedEventTypes) !== JSON.stringify(enabledEventTypes$.value)) {
      enabledEventTypes$.next(normalizedSelectedEventTypes);
    }
  }, [enabledEventTypes, enabledEventTypes$]);

  useEffect(() => {
    const initialRelatedEventTypes = getInitialRelatedEventTypesSelection(
      enabledEventTypes,
      filterParams
    );

    if (JSON.stringify(initialRelatedEventTypes) !== JSON.stringify(relatedEventTypes$.value)) {
      relatedEventTypes$.next(initialRelatedEventTypes);
    }
  }, [enabledEventTypes, filterParams, relatedEventTypes$]);

  useEffect(() => {
    if (falseFilter !== falseFilter$.value) {
      falseFilter$.next(falseFilter);
    }
  }, [falseFilter, falseFilter$]);

  useEffect(() => {
    if (JSON.stringify(availableIotEventTypes) !== JSON.stringify(availableIotEventTypes$.value)) {
      availableIotEventTypes$.next(availableIotEventTypes);
    }
  }, [availableIotEventTypes, availableIotEventTypes$]);

  useEffect(() => {
    const eventTypeIds = availableUserEventTypes.map((v) => v.id);
    const UserEventIds = eventTypeIds.filter((id) => !iotSeverities.has(id));
    if (JSON.stringify(UserEventIds) !== availableUserEventTypes$.value) {
      availableUserEventTypes$.next(UserEventIds);
    }
  }, [availableUserEventTypes, availableUserEventTypes$]);

  useEffect(() => {
    if (hideInactiveSystemEvents !== hideInactiveSystemEvents$.value) {
      hideInactiveSystemEvents$.next(hideInactiveSystemEvents);
    }
  }, [hideInactiveSystemEvents, hideInactiveSystemEvents$]);

  // Fetch unique remote sources when dateDomain or site changes
  useEffect(() => {
    // Point groups only feed the MACH remote-source filter, so skip the request otherwise.
    if (
      dateDomain &&
      dateDomain.length > 0 &&
      site &&
      enabledEventTypes.includes(EVENT_TYPE_MACH)
    ) {
      let from = dateDomain[0];
      let to = dateDomain[1] || Date.now();
      if (dateDomain?.[2]) {
        const relativeDomain = getRangeFromLabel(dateDomain[2]);
        from = relativeDomain.startDate;
        to = relativeDomain.endDate;
      }

      getUniquePointGroups({
        site: site.id,
        from,
        to,
      })
        .then((response) => {
          pointGroups$.next(response.values || []);
        })
        .catch((error) => {
          console.error('Failed to fetch unique point groups:', error);
          pointGroups$.next([]);
        });
    }
  }, [dateDomain, site, pointGroups$, enabledEventTypes]);

  const brushBucketCount$ = useMemo(() => new BehaviorSubject(brushBucketCount), []);
  const timelineBucketCount$ = useMemo(() => new BehaviorSubject(timelineBucketCount), []);
  useEffect(() => {
    if (brushBucketCount$.value !== brushBucketCount) {
      brushBucketCount$.next(brushBucketCount);
    }
  }, [brushBucketCount]);
  useEffect(() => {
    if (timelineBucketCount$.value !== timelineBucketCount) {
      timelineBucketCount$.next(timelineBucketCount);
    }
  }, [timelineBucketCount]);

  const activeComponentId$ = useSelect$(getFilteredActiveComponentId);
  const componentsUpdatedAt$ = useSelect$(getComponentsUpdatedAt);
  const components$ = useSelect$(getStaticComponents);
  const selectedScope = useMemo(
    () => componentScope?.selectedScope,
    [componentScope?.selectedScope]
  );

  const scopedToAllComps$ = useMemo(() => new BehaviorSubject(false), []);
  useEffect(() => {
    const sub = activeComponentId$.pipe(distinctUntilChanged()).subscribe((result) => {
      const isScopedToAllComps =
        (!result && String(selectedScope).includes('all')) || String(selectedScope) === 'all';
      if (isScopedToAllComps !== scopedToAllComps$.value) {
        scopedToAllComps$.next(isScopedToAllComps);
      }
    });
    return () => sub.unsubscribe();
  }, [selectedScope, activeComponentId$, scopedToAllComps$]);

  const scopedComponents$ = useMemo(() => {
    return combineLatest([components$, componentsUpdatedAt$, activeComponentId$]).pipe(
      map(([components, componentsUpdatedAt, activeComponent]) => {
        return getScopedComponents({
          components,
          componentsUpdatedAt,
          activeComponentId: activeComponent,
          componentScope,
        });
      })
    );
  }, [componentsUpdatedAt$, activeComponentId$, componentScope]);

  const scopedComponentIds$ = useMemo(() => new BehaviorSubject([]), []);
  const scopedIotComponentIds$ = useMemo(() => new BehaviorSubject([]), []);

  useEffect(() => {
    const sub = scopedComponents$.pipe(distinctUntilChanged()).subscribe((response) => {
      const iotComps = response.filter((c) => c.eventSources && c.eventSources.length > 0);
      scopedComponentIds$.next(response.map((c) => c.id));
      scopedIotComponentIds$.next(iotComps.map((c) => c.id));
    });
    return () => sub.unsubscribe();
  }, [scopedComponents$]);

  // panel-specific, local date domain
  const panelDateDomain$ = useMemo(() => new BehaviorSubject(dateDomain), []);

  // selected sub-domain within active domain
  const selectedDateDomain$ = useMemo(() => new BehaviorSubject(null), []);

  useEffect(() => {
    const sub = dateDomain$.pipe(distinctUntilChanged()).subscribe((response) => {
      if (response !== panelDateDomain$.value && response[1]) {
        panelDateDomain$.next([response[0], response[1]], response[2]);
      }
    });
    return () => sub.unsubscribe();
  }, [dateDomain$, panelDateDomain$]);

  useEffect(() => {
    const sub = selectedDateDomain$.pipe(distinctUntilChanged()).subscribe((response) => {
      if (
        response &&
        response === panelDateDomain$.value &&
        dateDomain$.value &&
        (response[0] <= dateDomain$.value[0] || response[1] >= dateDomain$.value[1])
      ) {
        selectedDateDomain$.next(null);
      }
    });
    return () => sub.unsubscribe();
  }, [dateDomain$.value, panelDateDomain$.value, selectedDateDomain$]);

  useEffect(() => {
    const sub = panelDateDomain$
      .pipe(distinctUntilChanged(), pairwise())
      .subscribe(([previous, current]) => {
        if (previous[0] !== current[0] || previous[1] !== current[1]) {
          selectedDateDomain$.next(null);
        }
      });
    return () => sub.unsubscribe();
  }, [panelDateDomain$, selectedDateDomain$]);

  const eventListOrdering$ = useMemo(() => new BehaviorSubject(eventListOrdering), []);

  useEffect(() => {
    if (eventListOrdering$.value !== eventListOrdering) {
      eventListOrdering$.next(eventListOrdering);
    }
  }, [eventListOrdering, eventListOrdering$]);

  const iotUpdated$ = useSelect$(getIotUpdated);

  // brush data
  const context_ = useMemo(
    () => ({
      site$,
      dateDomain$,
      hideInactiveSystemEvents$,
      filterText$,
      selectedDateDomain$,
      panelDateDomain$,
      activeComponentId$,
      scopedComponentIds$,
      scopedIotComponentIds$,
      scopedToAllComps$,
      brushBucketCount$,
      timelineBucketCount$,
      eventListOrdering$,
      iotUpdated$,
      availableUserEventTypes$,
      availableIotEventTypes$,
      selectedEventType$,
      enabledEventTypes$,
      relatedEventTypes$,
      falseFilter$,
      filterParams$: filterParameters$,
      pointGroups$,
      dtmpAlgorithmTypes$,
    }),
    [
      site$,
      dateDomain$,
      hideInactiveSystemEvents$,
      filterText$,
      selectedDateDomain$,
      panelDateDomain$,
      activeComponentId$,
      scopedComponentIds$,
      scopedIotComponentIds$,
      scopedToAllComps$,
      brushBucketCount$,
      timelineBucketCount$,
      eventListOrdering$,
      iotUpdated$,
      availableUserEventTypes$,
      availableIotEventTypes$,
      selectedEventType$,
      enabledEventTypes$,
      relatedEventTypes$,
      falseFilter$,
      filterParameters$,
      pointGroups$,
      dtmpAlgorithmTypes$,
    ]
  );
  return <context.Provider value={context_}>{children}</context.Provider>;
}

export function useGetFilterStateCallback() {
  const {
    filterText$,
    hideInactiveSystemEvents$,
    filterParams$,
    // falseFilter$,
  } = useContext(context);

  return useCallback(() => {
    return {
      text: filterText$.value,
      active: hideInactiveSystemEvents$.value,
      params: filterParams$.value,
      // falseFilter: falseFilter$.value,
    };
  }, [
    filterText$,
    hideInactiveSystemEvents$,
    filterParams$,
    // falseFilter$,
  ]);
}

// global date domain
export function useDateDomain() {
  const { dateDomain$ } = useContext(context);
  return useSubscribe(dateDomain$, dateDomain$.value);
}

export function useSetDateDomain() {
  const { dateDomain$ } = useContext(context);
  return useCallback((value) => dateDomain$.next(value), [dateDomain$]);
}

// panel-specific, local date domain
export function usePanelDateDomain() {
  const { panelDateDomain$ } = useContext(context);
  return useSubscribe(panelDateDomain$, panelDateDomain$.value);
}

export function useSetPanelDateDomain() {
  const { panelDateDomain$ } = useContext(context);
  return useCallback(
    (value) => {
      panelDateDomain$.next(value);
    },
    [panelDateDomain$]
  );
}

// selected sub-domain within active domain
export function useSelectedDateDomain() {
  const { selectedDateDomain$ } = useContext(context);
  return useSubscribe(selectedDateDomain$, selectedDateDomain$.value);
}

export function useOnSelectedDateDomainChange() {
  const { selectedDateDomain$ } = useContext(context);
  return useCallback(
    (response) => {
      const min = response?.[0];
      const max = response?.[1];
      const old = selectedDateDomain$.value;
      const oldMin = old?.[0];
      const oldMax = old?.[1];
      if (min !== oldMin || max !== oldMax) {
        selectedDateDomain$.next(response);
      }
    },
    [selectedDateDomain$]
  );
}

// component scope hooks
export function useScopedComponentIds() {
  const { scopedComponentIds$ } = useContext(context);
  return useSubscribe(scopedComponentIds$, scopedComponentIds$.value);
}

export function useScopedIotComponentIds() {
  const { scopedIotComponentIds$ } = useContext(context);
  return useSubscribe(scopedIotComponentIds$, scopedIotComponentIds$.value);
}

export function useScopedToAllComps() {
  const { scopedToAllComps$ } = useContext(context);
  return useSubscribe(scopedToAllComps$, scopedToAllComps$.value);
}

export function useUserEventTypes() {
  const userEventTypes$ = useSelect$(getUserEventTypes);
  return useSubscribe(userEventTypes$, userEventTypes$.value);
}

export function useStyling() {
  const theme = useTheme();

  return useMemo(() => {
    const textColor = theme === 'dark' ? colors.Grey60 : colors.Grey50;
    const tickStroke = theme === 'dark' ? colors.Grey80 : colors.Grey20;
    const background = theme === 'dark' ? colors.Grey90 : colors.Grey0;
    const indicatorStroke = colors.StatusRed;

    const tickLabelProperties = {
      textAnchor: 'middle',
      fontFamily: 'ABB, sans-serif',
      fontSize: 10.5,
      fill: textColor,
    };
    const yTickLabelProperties = () => ({
      ...tickLabelProperties,
      width: 70,
      verticalAnchor: 'middle',
      textAnchor: 'end',
    });
    const xTickLabelProperties = () => tickLabelProperties;
    const brushSelectionIndicator = {
      fill: 'steelblue',
      fillOpacity: 0.2,
      stroke: 'steelblue',
      strokeWidth: 3,
      strokeOpacity: 0.8,
    };

    return {
      textColor,
      tickStroke,
      background,
      indicatorStroke,
      xTickLabelProps: xTickLabelProperties,
      yTickLabelProps: yTickLabelProperties,
      brushSelectionIndicator,
    };
  }, [theme]);
}

export const handleUserEventErrors = (error, dispatch) => {
  console.error('Unable to fetch user events:', error);
  dispatch(checkIsOnline());
  dispatch(displayNotification(getNotification('getUserEvents', 'error')()));
  return {};
};

export const handleIotEventErrors = (error, dispatch) => {
  console.error('Unable to fetch iot events:', error);
  dispatch(checkIsOnline());

  let errorMessage;
  if (error.response?.data?.code === 'SOURCE_CONNECTION_FAILED') {
    errorMessage = 'Connection timed out. Retrying at next polling interval';
  }

  dispatch(displayNotification(getNotification('getIotEvents', 'error')(errorMessage)));
  return {};
};

export const extractBuckets = (response) =>
  response?.values?.[0]?.buckets ?? response?.values?.buckets ?? response?.buckets ?? [];

export const getScopedDtmpDesignations = ({
  scopedComponentIds,
  scopedToAllComps,
  componentHashmap,
}) => {
  if (!Array.isArray(scopedComponentIds) || scopedComponentIds.length === 0 || scopedToAllComps) {
    return null;
  }

  const designations = [
    ...new Set(
      scopedComponentIds
        .map((componentId) => componentHashmap?.[componentId]?.itemDesignation)
        .filter(Boolean)
    ),
  ];

  return designations.length > 0 ? designations : null;
};

export function useDateDomainHistogram() {
  const {
    site$,
    brushBucketCount$,
    activeComponentId$,
    scopedComponentIds$,
    scopedIotComponentIds$,
    scopedToAllComps$,
    hideInactiveSystemEvents$,
    filterText$,
    iotUpdated$,
    availableIotEventTypes$,
    availableUserEventTypes$,
    filterParams$,
    selectedEventType$,
    falseFilter$,
  } = useContext(context);
  const dispatch = useDispatch();
  const site = useSubscribe(site$, site$.value);
  const dateDomain = usePanelDateDomain();
  const filterParameters = useSubscribe(filterParams$, filterParams$.value);
  const hideInactiveSystemEvents = useSubscribe(
    hideInactiveSystemEvents$,
    hideInactiveSystemEvents$.value
  );
  const filterText = useSubscribe(filterText$, filterText$.value);
  const brushBucketCount = useSubscribe(brushBucketCount$, brushBucketCount$.value);
  const activeComp = useSubscribe(activeComponentId$);
  const scopedComponentIds = useSubscribe(scopedComponentIds$, scopedComponentIds$.value);
  const scopedIotComponentIds = useSubscribe(scopedIotComponentIds$, scopedIotComponentIds$.value);
  const scopedToAllComps = useSubscribe(scopedToAllComps$, scopedToAllComps$.value);
  const hasScopedFilter = !scopedToAllComps;
  const shouldFetchScoped = !hasScopedFilter || scopedComponentIds.length > 0;
  const iotUpdated = useSubscribe(iotUpdated$);
  const componentHashmap$ = useSelect$(getComponentHashmap);
  const componentHashmap = useSubscribe(componentHashmap$, componentHashmap$.value);
  const availableIotEventTypes = useSubscribe(
    availableIotEventTypes$,
    availableIotEventTypes$.value
  );
  const selectedEventType = useSubscribe(selectedEventType$, selectedEventType$.value);
  const falseFilter = useSubscribe(falseFilter$, falseFilter$.value);
  const availableUserEventTypes = useSubscribe(
    availableUserEventTypes$,
    availableUserEventTypes$.value
  );
  const scopedDtmpDesignations = useMemo(
    () =>
      getScopedDtmpDesignations({
        scopedComponentIds,
        scopedToAllComps,
        componentHashmap,
      }),
    [componentHashmap, scopedComponentIds, scopedToAllComps]
  );

  return useAsync(async () => {
    if (dateDomain.length > 0 && scopedComponentIds && scopedIotComponentIds && shouldFetchScoped) {
      let from = dateDomain[0];
      let to = dateDomain[1] || Date.now();
      if (dateDomain?.[2]) {
        const relativeDomain = getRangeFromLabel(dateDomain[2]);
        from = relativeDomain.startDate;
        to = relativeDomain.endDate;
      }
      const interval = Math.floor((to - from) / brushBucketCount);

      // For MACH event types: if specific types are filtered, use them; otherwise use available types from panel settings
      const availableMachTypes = availableIotEventTypes?.mach?.map((t) => t.id) || [];
      const defaultMachTypes = availableMachTypes.length > 0 ? availableMachTypes : undefined;
      const machTypeFilters = filterParameters?.mach?.eventType?.length
        ? filterParameters.mach.eventType
        : defaultMachTypes;

      const userEventTypeFilters =
        (filterParameters?.user?.type?.length || availableUserEventTypes.length > 0) &&
        filterParameters?.user?.type?.length
          ? filterParameters?.user?.type
          : availableUserEventTypes;
      const machRemoteSourceFilters = filterParameters?.mach?.remoteSource?.length
        ? filterParameters.mach.remoteSource
        : undefined;

      // DTMP-specific filters for histogram - use panel settings exclusions as default
      const availableDtmpTypes = availableIotEventTypes?.dtmp?.eventTypes?.map((t) => t.id) || [];
      const defaultDtmpTypes = availableDtmpTypes.length > 0 ? availableDtmpTypes : undefined;
      const dtmpAlgorithmTypeFilters = filterParameters?.dtmp?.algorithmTypes?.length
        ? filterParameters.dtmp.algorithmTypes
        : undefined;
      const dtmpEventTypeFilters = filterParameters?.dtmp?.eventType?.length
        ? filterParameters.dtmp.eventType
        : defaultDtmpTypes;
      const hasUserEventStatusFilter =
        filterParameters?.user?.status && filterParameters.user.status !== 'all';
      const hasUserEventActivityFilter =
        filterParameters?.user?.activity && filterParameters.user.activity !== 'all';

      const activeQuery = { active: hideInactiveSystemEvents };
      const [iot, userEvent] = await Promise.all([
        [EVENT_TYPE_DTMP, EVENT_TYPE_MACH].includes(selectedEventType)
          ? getEventBuckets({
              site: site.id,
              ...(selectedEventType === EVENT_TYPE_MACH && hasScopedFilter
                ? { components: scopedComponentIds }
                : {}),
              from,
              to,
              interval,
              ...(hideInactiveSystemEvents && selectedEventType === EVENT_TYPE_MACH
                ? activeQuery
                : {}),
              ...(filterText && { message: filterText }),
              ...(selectedEventType === EVENT_TYPE_MACH &&
                machTypeFilters && { types: machTypeFilters }),
              ...(selectedEventType === EVENT_TYPE_MACH &&
                machRemoteSourceFilters && { pointGroups: machRemoteSourceFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                dtmpAlgorithmTypeFilters && { algorithmTypes: dtmpAlgorithmTypeFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                dtmpEventTypeFilters && { types: dtmpEventTypeFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                scopedDtmpDesignations && {
                  deviatingReferenceDesignations: scopedDtmpDesignations,
                }),
              ...([EVENT_TYPE_DTMP, EVENT_TYPE_MACH].includes(selectedEventType) && {
                eventType: selectedEventType,
              }),
            }).then(
              (response) => response,
              (error) => handleIotEventErrors(error, dispatch)
            )
          : {},
        userEventTypeFilters.length > 0 &&
        selectedEventType === EVENT_TYPE_USER &&
        (scopedComponentIds.length > 0 || scopedToAllComps)
          ? getUserEvents({
              site: site.id,
              org: site.org,
              from,
              to,
              ...getUserIdentityFilters(filterParameters),
              ...(filterParameters?.user?.tags && { tags: filterParameters?.user?.tags }),
              ...(filterParameters?.user?.status &&
                filterParameters?.user?.status !== 'all' && {
                  state: [filterParameters?.user?.status],
                }),
              ...(filterParameters?.user?.activity &&
                filterParameters?.user?.activity !== 'all' && {
                  planned: filterParameters?.user?.activity === 'planned',
                }),
              ...(hasUserEventStatusFilter || hasUserEventActivityFilter ? { task: true } : {}),
              type: userEventTypeFilters,
              components: hasScopedFilter ? scopedComponentIds : undefined,
              withRecurring: true,
            }).then(
              (response) => response,
              (error) => handleUserEventErrors(error, dispatch)
            )
          : {},
      ]);
      const iotBuckets = extractBuckets(iot);
      const allUserEvents = userEvent.data || [];

      // Apply client-side text filtering for user events (searches name and description)
      const userEvents = filterText
        ? allUserEvents.filter((event) => {
            const searchText = filterText.toLowerCase();
            return (
              event.name?.toLowerCase().includes(searchText) ||
              event.description?.toLowerCase().includes(searchText)
            );
          })
        : allUserEvents;

      const histogram = {};

      for (const bucket of iotBuckets) {
        const fromMs = new Date(bucket.from).getTime();
        const key = (Math.floor(fromMs / interval) * interval).toString();
        histogram[key] = bucket.numEvents;
      }

      for (const event of userEvents) {
        const fromMs = new Date(event.from).getTime();
        const key = (Math.floor(fromMs / interval) * interval).toString();
        if (!histogram[key]) {
          histogram[key] = 0;
        }
        histogram[key] += 1;
      }
      const values = Object.values(histogram);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const buckets = Object.keys(histogram).map((key) => ({
        count: histogram[key],
        from: new Date(Number(key)),
        to: new Date(Number(key) + interval),
      }));
      return {
        min,
        max,
        buckets,
        dateDomain,
      };
    }
    return {
      min: undefined,
      max: undefined,
      buckets: [],
      dateDomain,
    };
  }, [
    dateDomain,
    site,
    brushBucketCount,
    activeComp,
    scopedComponentIds,
    scopedIotComponentIds,
    scopedToAllComps,
    hasScopedFilter,
    shouldFetchScoped,
    componentHashmap,
    scopedDtmpDesignations,
    hideInactiveSystemEvents,
    filterText,
    filterParameters,
    iotUpdated,
    availableIotEventTypes,
    availableUserEventTypes,
    selectedEventType,
    falseFilter,
  ]);
}

export function severityWeight(severity) {
  switch (severity) {
    case 'critical': {
      return 10;
    }
    case 'warning': {
      return 5;
    }
    default: {
      return 0;
    }
  }
}

export function timelineEventSort(a, b) {
  const weightA = severityWeight(a.severity);
  const weightB = severityWeight(b.severity);
  if (weightA === weightB) {
    return a.from < b.from ? -1 : 1;
  }
  return weightA < weightB ? -1 : 1;
}

export function useActiveTimelineData() {
  const {
    site$,
    timelineBucketCount$,
    activeComponentId$,
    scopedComponentIds$,
    scopedIotComponentIds$,
    scopedToAllComps$,
    hideInactiveSystemEvents$,
    filterText$,
    iotUpdated$,
    availableIotEventTypes$,
    availableUserEventTypes$,
    selectedEventType$,
    falseFilter$,
    filterParams$,
    dtmpAlgorithmTypes$,
  } = useContext(context);
  const dispatch = useDispatch();
  const site = useSubscribe(site$, site$.value);
  const filterParameters = useSubscribe(filterParams$, filterParams$.value);
  const hideInactiveSystemEvents = useSubscribe(
    hideInactiveSystemEvents$,
    hideInactiveSystemEvents$.value
  );
  const filterText = useSubscribe(filterText$, filterText$.value);
  const bucketCount = useSubscribe(timelineBucketCount$, timelineBucketCount$.value);
  const panelDateDomain = usePanelDateDomain();
  const selectedDateDomain = useSelectedDateDomain();
  const activeComp = useSubscribe(activeComponentId$);
  const scopedComponentIdsRaw = useSubscribe(scopedComponentIds$, scopedComponentIds$.value);
  const scopedIotComponentIdsRaw = useSubscribe(
    scopedIotComponentIds$,
    scopedIotComponentIds$.value
  );
  const scopedToAllComps = useSubscribe(scopedToAllComps$, scopedToAllComps$.value);
  const iotUpdated = useSubscribe(iotUpdated$);
  const availableIotEventTypes = useSubscribe(
    availableIotEventTypes$,
    availableIotEventTypes$.value
  );
  const selectedEventType = useSubscribe(selectedEventType$, selectedEventType$.value);
  const availableUserEventTypesRaw = useSubscribe(
    availableUserEventTypes$,
    availableUserEventTypes$.value
  );
  const falseFilter = useSubscribe(falseFilter$, falseFilter$.value);
  const componentHashmap$ = useSelect$(getComponentHashmap);
  const componentHashmap = useSubscribe(componentHashmap$, componentHashmap$.value);

  // Stabilize array references to prevent unnecessary re-fetches
  const scopedComponentIds = useMemo(
    () => scopedComponentIdsRaw,
    [JSON.stringify(scopedComponentIdsRaw)]
  );
  const scopedIotComponentIds = useMemo(
    () => scopedIotComponentIdsRaw,
    [JSON.stringify(scopedIotComponentIdsRaw)]
  );
  const availableUserEventTypes = useMemo(
    () => availableUserEventTypesRaw,
    [JSON.stringify(availableUserEventTypesRaw)]
  );
  const hasScopedFilter = !scopedToAllComps;
  const shouldFetchScoped = !hasScopedFilter || scopedComponentIds.length > 0;
  const scopedDtmpDesignations = useMemo(
    () =>
      getScopedDtmpDesignations({
        scopedComponentIds,
        scopedToAllComps,
        componentHashmap,
      }),
    [componentHashmap, scopedComponentIds, scopedToAllComps]
  );

  return useAsync(async () => {
    const dateDomain = selectedDateDomain || panelDateDomain;
    if (dateDomain.length > 0 && scopedComponentIds && scopedIotComponentIds && shouldFetchScoped) {
      let from = dateDomain[0];
      let to = dateDomain[1] || Date.now();
      if (dateDomain?.[2]) {
        const relativeDomain = getRangeFromLabel(dateDomain[2]);
        from = relativeDomain.startDate;
        to = relativeDomain.endDate;
      }
      const interval = Math.max(1, Math.floor((to - from) / bucketCount));

      // For MACH event types: if specific types are filtered, use them; otherwise use available types from panel settings
      const availableMachTypes = availableIotEventTypes?.mach?.map((t) => t.id) || [];
      const defaultMachTypes = availableMachTypes.length > 0 ? availableMachTypes : undefined;
      const machTypeFilters = filterParameters?.mach?.eventType?.length
        ? filterParameters.mach.eventType
        : defaultMachTypes;

      const userEventTypeFilters =
        (filterParameters?.user?.type?.length || availableUserEventTypes.length > 0) &&
        filterParameters?.user?.type?.length
          ? filterParameters?.user?.type
          : availableUserEventTypes;
      const machRemoteSourceFilters = filterParameters?.mach?.remoteSource?.length
        ? filterParameters.mach.remoteSource
        : undefined;

      // DTMP-specific filters for timeline - use panel settings exclusions as default
      const availableDtmpTypes = availableIotEventTypes?.dtmp?.eventTypes?.map((t) => t.id) || [];
      const defaultDtmpTypes = availableDtmpTypes.length > 0 ? availableDtmpTypes : undefined;
      const dtmpAlgorithmTypeFilters = filterParameters?.dtmp?.algorithmType?.length
        ? filterParameters.dtmp.algorithmType
        : undefined;
      const dtmpEventTypeFilters = filterParameters?.dtmp?.eventType?.length
        ? filterParameters.dtmp.eventType
        : defaultDtmpTypes;
      const hasUserEventStatusFilter =
        filterParameters?.user?.status && filterParameters.user.status !== 'all';
      const hasUserEventActivityFilter =
        filterParameters?.user?.activity && filterParameters.user.activity !== 'all';

      const activeQuery = { active: hideInactiveSystemEvents };

      const result = await Promise.all([
        [EVENT_TYPE_DTMP, EVENT_TYPE_MACH].includes(selectedEventType)
          ? getEventBuckets({
              site: site.id,
              ...(selectedEventType === EVENT_TYPE_MACH && hasScopedFilter
                ? { components: scopedComponentIds }
                : {}),
              from,
              to,
              interval,
              ...(hideInactiveSystemEvents && selectedEventType === EVENT_TYPE_MACH
                ? activeQuery
                : {}),
              ...(filterText && { message: filterText }),
              ...(selectedEventType === EVENT_TYPE_MACH &&
                machTypeFilters && { types: machTypeFilters }),
              filterFalse: falseFilter,
              ...(selectedEventType === EVENT_TYPE_MACH &&
                machRemoteSourceFilters && { pointGroups: machRemoteSourceFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                dtmpAlgorithmTypeFilters && { algorithmTypes: dtmpAlgorithmTypeFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                dtmpEventTypeFilters && { types: dtmpEventTypeFilters }),
              ...(selectedEventType === EVENT_TYPE_DTMP &&
                scopedDtmpDesignations && {
                  deviatingReferenceDesignations: scopedDtmpDesignations,
                }),
              ...([EVENT_TYPE_DTMP, EVENT_TYPE_MACH].includes(selectedEventType) && {
                eventType: selectedEventType,
              }),
            }).then(
              (response) => response,
              (error) => handleIotEventErrors(error, dispatch)
            )
          : {},
        userEventTypeFilters.length > 0 &&
        selectedEventType === EVENT_TYPE_USER &&
        (scopedComponentIds.length > 0 || scopedToAllComps)
          ? getUserEvents({
              site: site.id,
              org: site.org,
              from,
              to,
              ...getUserIdentityFilters(filterParameters),
              ...(filterParameters?.user?.tags && { tags: filterParameters?.user?.tags }),
              ...(filterParameters?.user?.status &&
                filterParameters?.user?.status !== 'all' && {
                  state: [filterParameters?.user?.status],
                }),
              ...(filterParameters?.user?.activity &&
                filterParameters?.user?.activity !== 'all' && {
                  planned: filterParameters?.user?.activity === 'planned',
                }),
              ...(hasUserEventStatusFilter || hasUserEventActivityFilter ? { task: true } : {}),
              type: userEventTypeFilters,
              components: hasScopedFilter ? scopedComponentIds : undefined,
              withRecurring: true,
            }).then(
              (response) => response,
              (error) => handleUserEventErrors(error, dispatch)
            )
          : {},
      ]);

      const iotBuckets = extractBuckets(result?.[0]);
      const allUserEvents = result?.[1]?.data || [];

      // Apply client-side text filtering for user events (searches name and description)
      const filteredUserEvents = filterText
        ? allUserEvents.filter((event) => {
            const searchText = filterText.toLowerCase();
            return (
              event.name?.toLowerCase().includes(searchText) ||
              event.description?.toLowerCase().includes(searchText)
            );
          })
        : allUserEvents;

      const userEvents = [...filteredUserEvents].toSorted(timelineEventSort);

      const userEventTypes = userEvents
        .reduce((acc, event) => {
          if (!acc.includes(event.type)) {
            acc.push(event.type);
          }
          return acc;
        }, [])
        .toSorted()
        .toReversed();

      const dtmpAlgorithmTypes = (
        config.onPremise
          ? // top-level DTMP algorithm types are returned from the API for on-premise deployments, so use those if available
            (result?.[0]?.values?.[0]?.algorithmTypes ?? [])
          : // else we extract the algorithm types from the returned events for cloud deployments
            [
              ...new Set(
                iotBuckets
                  .flatMap((bucket) => bucket.events || [])
                  .map((event) => event.algorithmType)
                  .filter(Boolean)
              ),
            ]
      ).toSorted((left, right) => left.localeCompare(right));

      if (JSON.stringify(dtmpAlgorithmTypes) !== JSON.stringify(dtmpAlgorithmTypes$.value)) {
        dtmpAlgorithmTypes$.next(dtmpAlgorithmTypes);
      }

      return {
        limitedUserEvents: !!result?.[1]?.limited,
        userEvents,
        userEventTypes,
        iotBuckets,
        dateDomain,
      };
    }

    return {
      limitedUserEvents: false,
      userEvents: [],
      userEventTypes: [],
      iotBuckets: [],
      dateDomain,
    };
  }, [
    site,
    panelDateDomain,
    selectedDateDomain,
    bucketCount,
    activeComp,
    scopedComponentIds,
    scopedIotComponentIds,
    scopedToAllComps,
    hasScopedFilter,
    shouldFetchScoped,
    componentHashmap,
    scopedDtmpDesignations,
    hideInactiveSystemEvents,
    filterText,
    iotUpdated,
    availableIotEventTypes,
    availableUserEventTypes,
    selectedEventType,
    falseFilter,
    filterParameters,
  ]);
}

export function useDtmpAlgorithmTypes() {
  const { dtmpAlgorithmTypes$ } = useContext(context);
  return useSubscribe(dtmpAlgorithmTypes$, dtmpAlgorithmTypes$.value);
}

const descSort = (a, b) => (a.timestamp > b.timestamp ? -1 : 1);
const ascSort = (a, b) => (a.timestamp < b.timestamp ? -1 : 1);

export function sortByTimestamp(ordering) {
  if (ordering === 'desc') {
    return descSort;
  }
  return ascSort;
}

export function inRange(timestamp, ordering, start, end) {
  if (ordering === 'desc') {
    return timestamp >= end && timestamp <= start;
  }
  return timestamp >= start && timestamp <= end;
}

export function useActiveListEvents(limit = 100, scoped = true, isLimitedList = false) {
  const {
    site$,
    activeComponentId$,
    scopedIotComponentIds$,
    scopedComponentIds$,
    scopedToAllComps$,
    eventListOrdering$,
    hideInactiveSystemEvents$,
    filterText$,
    iotUpdated$,
    availableIotEventTypes$,
    selectedEventType$,
    enabledEventTypes$,
    relatedEventTypes$,
    availableUserEventTypes$,
    falseFilter$,
    filterParams$,
  } = useContext(context);
  const dispatch = useDispatch();
  const site = useSubscribe(site$, site$.value);
  const setLoading = useSetLoading();
  const panelDateDomain = usePanelDateDomain();
  const selectedDateDomain = useSelectedDateDomain();
  const activeComp = useSubscribe(activeComponentId$);
  const scopedComponentIdsRaw = useSubscribe(scopedComponentIds$, scopedComponentIds$.value);
  const scopedIotComponentIdsRaw = useSubscribe(
    scopedIotComponentIds$,
    scopedIotComponentIds$.value
  );
  const scopedToAllComps = useSubscribe(scopedToAllComps$, scopedToAllComps$.value);
  const iotUpdated = useSubscribe(iotUpdated$);
  const eventListOrdering = useSubscribe(eventListOrdering$);
  const filterParameters = useSubscribe(filterParams$, filterParams$.value);
  const hideInactiveSystemEvents = useSubscribe(
    hideInactiveSystemEvents$,
    hideInactiveSystemEvents$.value
  );
  const filterText = useSubscribe(filterText$, filterText$.value);
  const availableIotEventTypes = useSubscribe(
    availableIotEventTypes$,
    availableIotEventTypes$.value
  );
  const availableUserEventTypesRaw = useSubscribe(
    availableUserEventTypes$,
    availableUserEventTypes$.value
  );
  const selectedEventType = useSubscribe(selectedEventType$, selectedEventType$.value);
  const enabledEventTypesRaw = useSubscribe(enabledEventTypes$, enabledEventTypes$.value);
  const relatedEventTypesRaw = useSubscribe(relatedEventTypes$, relatedEventTypes$.value);
  const falseFilter = useSubscribe(falseFilter$, falseFilter$.value);
  const componentHashmap$ = useSelect$(getComponentHashmap);
  const componentHashmap = useSubscribe(componentHashmap$, componentHashmap$.value);

  // Stabilize array references to prevent unnecessary re-renders
  const scopedComponentIds = useMemo(
    () => scopedComponentIdsRaw,
    [JSON.stringify(scopedComponentIdsRaw)]
  );
  const scopedIotComponentIds = useMemo(
    () => scopedIotComponentIdsRaw,
    [JSON.stringify(scopedIotComponentIdsRaw)]
  );
  const availableUserEventTypes = useMemo(
    () => availableUserEventTypesRaw,
    [JSON.stringify(availableUserEventTypesRaw)]
  );
  const enabledEventTypes = useMemo(
    () => normalizeEventTypeSelection(enabledEventTypesRaw),
    [JSON.stringify(enabledEventTypesRaw)]
  );
  const relatedEventTypes = useMemo(
    () => normalizeEventTypeSelection(relatedEventTypesRaw),
    [JSON.stringify(relatedEventTypesRaw)]
  );
  const hasScopedFilter = !scopedToAllComps;
  const shouldFetchScoped = !hasScopedFilter || scopedComponentIds.length > 0;
  const scopedDtmpDesignations = useMemo(
    () =>
      getScopedDtmpDesignations({
        scopedComponentIds,
        scopedToAllComps,
        componentHashmap,
      }),
    [componentHashmap, scopedComponentIds, scopedToAllComps]
  );

  const dateDomain = selectedDateDomain || panelDateDomain || [];
  let from = dateDomain.length > 0 && dateDomain[0];
  let to = dateDomain.length > 0 && dateDomain[1] ? dateDomain[1] : Date.now();
  if (dateDomain?.[2]) {
    const relativeDomain = getRangeFromLabel(dateDomain[2]);
    from = relativeDomain.startDate;
    to = relativeDomain.endDate;
  }
  const items$ = useMemo(() => new BehaviorSubject([]), []);
  const hasNextPage$ = useMemo(() => new BehaviorSubject(true), []);
  const totalEventCount$ = useMemo(() => new BehaviorSubject(-1), []);
  const getNextPage$ = useMemo(() => new BehaviorSubject(), []);

  useAsync(async () => {
    if (!from || !scopedComponentIds || !scopedIotComponentIds) return;

    items$.next([]);
    getNextPage$.next();

    if (!shouldFetchScoped) {
      hasNextPage$.next(false);
      totalEventCount$.next(0);
      return;
    }

    const hasPersistedShowFlags =
      typeof filterParameters?.mach?.showMachEvents === 'boolean' ||
      typeof filterParameters?.dtmp?.showDtmpEvents === 'boolean' ||
      typeof filterParameters?.user?.showUserEvents === 'boolean';

    const relatedEventTypesFromPersistedFlags = [
      filterParameters?.mach?.showMachEvents && EVENT_TYPE_MACH,
      filterParameters?.dtmp?.showDtmpEvents && EVENT_TYPE_DTMP,
      filterParameters?.user?.showUserEvents && EVENT_TYPE_USER,
    ]
      .filter(Boolean)
      .filter((eventType) => enabledEventTypes.includes(eventType));

    let eventTypesForFetch = hasPersistedShowFlags
      ? relatedEventTypesFromPersistedFlags
      : relatedEventTypes;
    if (selectedEventType) {
      eventTypesForFetch = [selectedEventType];
    }

    const userEventsEnabled = eventTypesForFetch.includes(EVENT_TYPE_USER);
    const machEventsEnabled = eventTypesForFetch.includes(EVENT_TYPE_MACH);
    const dtmpEventsEnabled = eventTypesForFetch.includes(EVENT_TYPE_DTMP);
    const userEventTypeFilters =
      (filterParameters?.user?.type?.length || availableUserEventTypes.length > 0) &&
      filterParameters?.user?.type?.length
        ? filterParameters?.user?.type
        : availableUserEventTypes;

    const dtmpAlgorithmTypeFilters = filterParameters?.dtmp?.algorithmType?.length
      ? filterParameters.dtmp.algorithmType
      : undefined;

    // For DTMP event types: if specific types are filtered, use them; otherwise use available types from panel settings
    const availableDtmpTypes = availableIotEventTypes?.dtmp?.eventTypes?.map((t) => t.id) || [];
    const defaultDtmpTypes = availableDtmpTypes.length > 0 ? availableDtmpTypes : undefined;
    const dtmpEventTypeFilters = filterParameters?.dtmp?.eventType?.length
      ? filterParameters.dtmp.eventType
      : defaultDtmpTypes;

    // For MACH event types: if specific types are filtered, use them; otherwise use available types from panel settings
    const availableMachTypes = availableIotEventTypes?.mach?.map((t) => t.id) || [];
    const defaultMachTypes = availableMachTypes.length > 0 ? availableMachTypes : undefined;
    const machTypeFilters = filterParameters?.mach?.eventType?.length
      ? filterParameters.mach.eventType
      : defaultMachTypes;

    const machRemoteSourceFilters = filterParameters?.mach?.remoteSource?.length
      ? filterParameters.mach.remoteSource
      : undefined;
    const hasUserEventStatusFilter =
      filterParameters?.user?.status && filterParameters.user.status !== 'all';
    const hasUserEventActivityFilter =
      filterParameters?.user?.activity && filterParameters.user.activity !== 'all';

    const userEventQuery = {
      site: site.id,
      org: site.org,
      from,
      to,
      ...getUserIdentityFilters(filterParameters),
      ...(filterParameters?.user?.tags && { tags: filterParameters?.user?.tags }),
      ...(filterParameters?.user?.status &&
        filterParameters?.user?.status !== 'all' && { state: [filterParameters?.user?.status] }),
      ...(filterParameters?.user?.activity &&
        filterParameters?.user?.activity !== 'all' && {
          planned: filterParameters?.user?.activity === 'planned',
        }),
      ...(hasUserEventStatusFilter || hasUserEventActivityFilter ? { task: true } : {}),
      type: userEventTypeFilters,
      components: !scoped || !hasScopedFilter ? undefined : scopedComponentIds,
      withRecurring: true,
    };

    const activeQuery = { active: hideInactiveSystemEvents };
    const buildIotEventQuery = (selectedIotEventType) => ({
      site: site.id,
      ...(selectedIotEventType === EVENT_TYPE_MACH && scoped && hasScopedFilter
        ? { components: scopedComponentIds }
        : {}),
      limit,
      order: eventListOrdering,
      from,
      to,
      ...(hideInactiveSystemEvents && selectedIotEventType === EVENT_TYPE_MACH ? activeQuery : {}),
      ...(filterText && { message: filterText }),
      ...(selectedIotEventType === EVENT_TYPE_MACH &&
        machTypeFilters &&
        machTypeFilters.length > 0 && { types: machTypeFilters }),
      filterFalse: falseFilter,
      ...(selectedIotEventType === EVENT_TYPE_MACH &&
        machRemoteSourceFilters && { pointGroups: machRemoteSourceFilters }),
      ...(selectedIotEventType === EVENT_TYPE_DTMP &&
        dtmpAlgorithmTypeFilters && { algorithmTypes: dtmpAlgorithmTypeFilters }),
      ...(selectedIotEventType === EVENT_TYPE_DTMP &&
        dtmpEventTypeFilters && { types: dtmpEventTypeFilters }),
      ...(selectedIotEventType === EVENT_TYPE_DTMP &&
        scoped &&
        hasScopedFilter &&
        scopedDtmpDesignations && {
          deviatingReferenceDesignations: scopedDtmpDesignations,
        }),
      eventType: selectedIotEventType,
    });
    const isLimitedListWithUserEventsEnabledAndFilters =
      isLimitedList &&
      eventTypesForFetch.length > 1 &&
      userEventsEnabled &&
      userEventTypeFilters.length > 0;
    const userEventResponse =
      (isLimitedListWithUserEventsEnabledAndFilters ||
        selectedEventType === EVENT_TYPE_USER ||
        (!selectedEventType && userEventsEnabled)) &&
      (!scoped || scopedComponentIds.length > 0 || scopedToAllComps)
        ? await (async () =>
            getUserEvents(userEventQuery).then(
              (response) => response,
              (error) => handleUserEventErrors(error, dispatch)
            ))()
        : { data: [] };
    const allUserEvents = userEventResponse.data || [];

    // Apply client-side text filtering for user events (searches name and description)
    const userEvents = filterText
      ? allUserEvents.filter((event) => {
          const searchText = filterText.toLowerCase();
          return (
            event.name?.toLowerCase().includes(searchText) ||
            event.description?.toLowerCase().includes(searchText)
          );
        })
      : allUserEvents;
    const endUserEvents = userEvents
      .filter((event) => event.to && event.to > event.from)
      .map((event) => ({
        timestamp: new Date(event.to).getTime(),
        id: event.id,
        event,
        type: 'user-end',
      }));

    const startUserEvents = userEvents.map((event) => ({
      timestamp: new Date(event.from).getTime(),
      id: event.id,
      event,
      type: event.to && event.to > event.from ? 'user-start' : 'user',
    }));

    if (isLimitedList && eventTypesForFetch.length > 1) {
      // Limited list has no tabs. When multiple families are configured, fetch each selected
      // family explicitly and merge them into the single time-ordered feed the UI renders.
      const limitedListIotQueries = [
        ...(machEventsEnabled ? [buildIotEventQuery(EVENT_TYPE_MACH)] : []),
        ...(dtmpEventsEnabled ? [buildIotEventQuery(EVENT_TYPE_DTMP)] : []),
      ];
      const limitedListIotResponses = await Promise.all(
        limitedListIotQueries.map((iotEventQuery) =>
          getEvents(iotEventQuery).then(
            (response) => response,
            (error) => handleIotEventErrors(error, dispatch)
          )
        )
      );
      const limitedListIotEvents = limitedListIotResponses.flatMap((response) =>
        (response?.values || []).map((event) => ({
          timestamp: new Date(event.timestamp).getTime(),
          id: event.id,
          event,
          type: 'iot',
        }))
      );
      const mergedLimitedListEvents = [
        ...startUserEvents,
        ...endUserEvents,
        ...limitedListIotEvents,
      ].toSorted(sortByTimestamp(eventListOrdering));

      items$.next(mergedLimitedListEvents);
      hasNextPage$.next(false);
      totalEventCount$.next(mergedLimitedListEvents.length);
      setLoading(false);
      return;
    }

    hasNextPage$.next(true);
    totalEventCount$.next(-1);
    let sourceQueryOptionsByEventType = {};
    let lastOrdering;
    let lastEventTypeFilter = selectedEventType;
    let loadedIotEvents = [];
    let page = 1;
    const pagedIotEventTypes =
      selectedEventType && selectedEventType !== EVENT_TYPE_USER
        ? [selectedEventType]
        : eventTypesForFetch.filter((eventType) => eventType !== EVENT_TYPE_USER);

    async function next() {
      if (getNextPage$.value) {
        getNextPage$.next();
      }
      // Reset if ordering changes OR if event type filter changes
      if (eventListOrdering !== lastOrdering || selectedEventType !== lastEventTypeFilter) {
        // if ordering or filter changes we reset
        sourceQueryOptionsByEventType = {};
        loadedIotEvents = [];
        lastEventTypeFilter = selectedEventType;
        page = 1;
      }

      const pagedIotResponses = await Promise.all(
        pagedIotEventTypes.map((eventType) =>
          getEvents({
            ...buildIotEventQuery(eventType),
            from,
            to,
            order: eventListOrdering,
            sourceOptions: sourceQueryOptionsByEventType[eventType],
            page,
          }).then(
            (response) => response,
            (error) => handleIotEventErrors(error, dispatch)
          )
        )
      );

      const hasNext = pagedIotResponses.some(
        (response) => (response?.values || []).length === limit
      );
      hasNextPage$.next(hasNext);
      totalEventCount$.next(
        pagedIotResponses.reduce((total, response) => total + (response?.total || 0), 0) +
          userEvents.length
      );

      const nextIotEvents = pagedIotResponses.flatMap((response) =>
        (response?.values || []).map((event) => ({
          timestamp: new Date(event.timestamp).getTime(),
          id: event.id,
          event,
          type: 'iot',
        }))
      );

      loadedIotEvents = [...loadedIotEvents, ...nextIotEvents];
      items$.next(
        [...startUserEvents, ...endUserEvents, ...loadedIotEvents].toSorted(
          sortByTimestamp(eventListOrdering)
        )
      );

      if (hasNext) {
        getNextPage$.next(next);
      }

      lastOrdering = eventListOrdering;
      lastEventTypeFilter = selectedEventType;
      sourceQueryOptionsByEventType = pagedIotResponses.reduce(
        (nextSourceOptions, response, index) => {
          if (response?.sourceOptions) {
            nextSourceOptions[pagedIotEventTypes[index]] = response.sourceOptions;
          }
          return nextSourceOptions;
        },
        {}
      );
      page += 1;
    }
    setLoading(true);
    if (eventListOrdering) {
      await next();
      setLoading(false);
    }
  }, [
    from,
    to,
    activeComp,
    scopedComponentIds,
    scopedIotComponentIds,
    scopedToAllComps,
    hasScopedFilter,
    shouldFetchScoped,
    componentHashmap,
    scopedDtmpDesignations,
    eventListOrdering,
    site,
    hideInactiveSystemEvents,
    filterText,
    items$,
    getNextPage$,
    hasNextPage$,
    totalEventCount$,
    setLoading,
    iotUpdated,
    panelDateDomain,
    selectedDateDomain,
    availableIotEventTypes,
    availableUserEventTypes,
    falseFilter,
    enabledEventTypes,
    relatedEventTypes,
    selectedEventType,
    filterParameters,
  ]);

  const events = useSubscribe(items$, []);
  const hasNextPage = useSubscribe(hasNextPage$, hasNextPage$.value);
  const totalEventCount = useSubscribe(totalEventCount$, totalEventCount$.value);
  const handleNext = useCallback(() => {
    if (!hasNextPage$.value) {
      return Promise.resolve();
    }
    const getNext = getNextPage$.value;
    if (getNext) {
      return getNext();
    }
    return Promise.resolve();
  }, [getNextPage$, hasNextPage$]);
  return {
    events,
    next: handleNext,
    hasNextPage,
    totalEventCount,
  };
}

const zScale = scaleOrdinal({
  domain: ['critical', 'warning', 'minor', 'info', 'internal'],
  range: ['#F03040', '#FFD800', '#3366FF', '#3366FF', '#3366FF'],
});

export function useZScale() {
  return zScale;
}

// filterParams hold the event type specific filters ({ mach: {}, user: {}, dtmp: {} })
// and are used to build the query for fetching events as well as for rendering the filter UI (e.g. which event types have filters applied)
export function useFilterParameters() {
  const { filterParams$ } = useContext(context);
  const [value, setValue] = useState(filterParams$.value);
  useEffect(() => {
    const subscription = filterParams$.subscribe(setValue);
    return () => subscription.unsubscribe();
  }, [filterParams$]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        filterParams$.next(event.target.value);
      } else {
        filterParams$.next(event);
      }
    },
    [filterParams$]
  );
  return [value, onChange];
}

export function usePointGroups() {
  const { pointGroups$ } = useContext(context);
  return useSubscribe(pointGroups$, pointGroups$.value);
}

// selectedEventType is used when the panel is configured with multiple event types enabled,
// and is used to determine which event type's data to show in the timeline (e.g. when user switches between tabs in the UI)
export function useSelectedEventType() {
  const { selectedEventType$ } = useContext(context);
  const [value, setValue] = useState(selectedEventType$.value);
  useEffect(() => {
    selectedEventType$.subscribe(setValue);
  }, [selectedEventType$, setValue]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        selectedEventType$.next(event.target.value);
      } else {
        selectedEventType$.next(event);
      }
    },
    [selectedEventType$]
  );
  return [value, onChange];
}

// enabledEventTypes are used at the panel level to enable/disable events (e.g. only specific event tabs, or single event type panel)
// and to determine which event type filters to show in the UI
export function useEnabledEventTypes() {
  const { enabledEventTypes$ } = useContext(context);
  const [value, setValue] = useState(enabledEventTypes$.value);
  useEffect(() => {
    enabledEventTypes$.subscribe(setValue);
  }, [enabledEventTypes$, setValue]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        enabledEventTypes$.next(event.target.value);
      } else {
        enabledEventTypes$.next(event);
      }
    },
    [enabledEventTypes$]
  );
  return [value, onChange];
}

export function useRelatedEventTypes() {
  const { relatedEventTypes$ } = useContext(context);
  const [value, setValue] = useState(relatedEventTypes$.value);
  useEffect(() => {
    relatedEventTypes$.subscribe(setValue);
  }, [relatedEventTypes$, setValue]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        relatedEventTypes$.next(event.target.value);
      } else {
        relatedEventTypes$.next(event);
      }
    },
    [relatedEventTypes$]
  );
  return [value, onChange];
}

export function useFalseFilter() {
  const { falseFilter$ } = useContext(context);
  const [value, setValue] = useState(falseFilter$.value);
  useEffect(() => {
    falseFilter$.subscribe(setValue);
  }, [falseFilter$, setValue]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        falseFilter$.next(event.target.value);
      } else {
        falseFilter$.next(event);
      }
    },
    [falseFilter$]
  );
  return [value, onChange];
}

export function useFilterText() {
  const { filterText$ } = useContext(context);
  const [value, setValue] = useState(filterText$.value);
  useEffect(() => {
    filterText$.subscribe(setValue);
  }, [filterText$, setValue]);
  const onChange = useCallback(
    (event) => {
      if (event?.target) {
        filterText$.next(event.target.value);
      } else {
        filterText$.next(event);
      }
    },
    [filterText$]
  );
  return [value, onChange];
}

export function useSetEventListOrdering() {
  const { eventListOrdering$ } = useContext(context);
  const [value, setValue] = useState(eventListOrdering$.value);
  useEffect(() => {
    eventListOrdering$.subscribe(setValue);
  }, [eventListOrdering$, setValue]);
  const onChange = useCallback((value_) => eventListOrdering$.next(value_), [eventListOrdering$]);
  return [value, onChange];
}

const hoverContext = createContext(new BehaviorSubject());

export function useHoverContext(eventId) {
  const hovering$ = useContext(hoverContext);
  const setHovering = useCallback(
    (isHovering) => {
      if (!eventId) {
        return;
      }
      if (isHovering) {
        hovering$.next(eventId);
      } else if (hovering$.value === eventId) {
        hovering$.next();
      }
    },
    [hovering$, eventId]
  );
  const hovering = useSubscribe(hovering$);
  useEffect(() => () => setHovering(false), [setHovering]);
  return [eventId && eventId === hovering, setHovering];
}

export function useDateDomainSource(panelId) {
  const today$ = useMemo(() => new BehaviorSubject(Date.now()), []);
  const pollingRangeDate$ = useSelect$((state) => getPollingDateRange(state, panelId), []);
  const refresh = useCallback(() => today$.next(Date.now()), [today$]);
  const dateDomain$ = useMemo(
    () =>
      pollingRangeDate$.pipe(
        switchMap(({ startDate, endDate, label }) =>
          today$.pipe(map(() => [startDate, endDate, label]))
        )
      ),
    [today$, pollingRangeDate$]
  );
  const dateDomain = useSubscribe(dateDomain$, []);
  return [dateDomain, refresh];
}

export function useHideInactiveSystemEvents() {
  const { hideInactiveSystemEvents$ } = useContext(context);
  const [value, setValue] = useState(hideInactiveSystemEvents$.value);
  useEffect(() => {
    hideInactiveSystemEvents$.subscribe(setValue);
  }, [hideInactiveSystemEvents$, setValue]);
  const setHideInactiveSystemEvents = useCallback(
    (hide) => {
      if (hide !== hideInactiveSystemEvents$.value) {
        hideInactiveSystemEvents$.next(hide);
      }
    },
    [hideInactiveSystemEvents$]
  );
  return [value, setHideInactiveSystemEvents];
}

export function useIotEventTypes() {
  const { availableIotEventTypes$ } = useContext(context);
  return useSubscribe(availableIotEventTypes$);
}
